import {useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonIcon from '@mui/icons-material/Person';
import {Divider, Grid, Typography} from '@mui/material';

import {ScreeningModuleBanner} from '../../../components/Banner/ScreeningModuleBanner/ScreeningModuleBanner';
import {EmpionButton} from '../../../components/EmpionButton/EmpionButton';
import {LoadingContainer} from '../../../components/Loading/LoadingContainer';
import {CompanyContext} from '../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {ApplicantScreeningFilters} from '../../../features/company/screening/ApplicantScreeningFilters/ApplicantScreeningFilters';
import {ApplicantsScreeningTable} from '../../../features/company/screening/ApplicantsScreeningTable/ApplicantsScreeningTable';
import {BatchInvitationsTable} from '../../../features/company/screening/BatchInvitationsTable/BatchInvitationsTable';
import {BatchInvitationDetailsModal} from '../../../features/company/screening/components/BatchInvitationDetailsModal';
import {BatchInvitationsModal} from '../../../features/company/screening/components/BatchInvitationsModal';
import {CompanyScreeningHeader} from '../../../features/company/screening/components/CompanyScreeningHeader';
import {InviteApplicantModal} from '../../../features/company/screening/components/InviteApplicantModal';
import {ScreeningStatusCountDisplay} from '../../../features/company/screening/components/ScreeningStatusCountDisplay';
import {ScreeningView, ScreeningViewToggle} from '../../../features/company/screening/components/ScreeningViewToggle';
import {viewToggleContentStyles} from '../../../features/company/screening/components/ScreeningViewToggle.styles';
import {useBatchInvitationsProgress} from '../../../features/company/screening/hooks/useBatchInvitationsProgress';
import {useScreeningApplicantSorting} from '../../../hooks/useScreeningApplicantSorting';
import {ModuleTypes} from '../../../types/CompanyProductConfiguration/CompanyModule';
import {Option} from '../../../types/shared-types';
import {CompanyAdminRoleData} from '../../../types/User';
import {isCompanyModuleActive} from '../../../utils/companyModuleUtils/isCompanyModuleActive';

export const CompanyCultureChecksPage = () => {
  const {t} = useTranslation();
  const {t: tr} = useTranslation('translation', {keyPrefix: 'screeningModule.tabFilters'});

  const {productConfiguration, cultureInformation} = useContext(CompanyContext);
  const isScreeningModuleActive = isCompanyModuleActive(
    ModuleTypes.ScreeningApplicants,
    productConfiguration.data?.companyModules
  );
  const isCultureInformationAvailable = !!cultureInformation.data?.cultureType;

  const screeningTooltipText = !isScreeningModuleActive
    ? t('screeningModule.featureDisabled')
    : t('screeningModule.moduleTooltip');

  const [searchParams, setSearchParams] = useSearchParams();
  const [inviteApplicant, setInviteApplicant] = useState<boolean>(false);
  const [batchInvitationsOpen, setBatchInvitationsOpen] = useState<boolean>(false);
  const [lastConfirmedBatchId, setLastConfirmedBatchId] = useState<string | null>(null);

  const {currentUser} = useContext(UserContext);
  const currentLanguage = useContext(LanguageContext);
  const companyId = (currentUser?.role.data as CompanyAdminRoleData)?.companyId || '';
  const selectedView = (searchParams.get('view') as ScreeningView) || 'applicants';
  const isApplicantsView = selectedView === 'applicants';

  const createOptions = (options: string[]) => {
    return options.map((option) => ({label: option, id: option}));
  };

  const {
    screeningApplicants,
    pagination,
    hasNext,
    getNext,
    totalCountPerStatus,
    screeningIsLoading,
    isLoading,
    refreshData,
    refetchFilters,
    filtersOptions,
    statusOptions,
    filterBy,
    initialStatuses,
    initialLocations,
    initialPositions,
    initialDepartments,
    tablePage,
    setTablePage,
  } = useScreeningApplicantSorting({
    isEnabled: isScreeningModuleActive && isCultureInformationAvailable,
    companyId,
  });

  const statuses: Option[] = useMemo(() => {
    return statusOptions.map((status) => ({id: status, label: tr(status)}));
  }, [tr, statusOptions]);

  const {
    batchInvitations,
    isListLoading: isBatchInvitationsLoading,
    refetchBatchInvitations,
    isDetailsOpen,
    openDetails,
    closeDetails,
    detailsResponse,
    isDetailsLoading,
    detailsError,
  } = useBatchInvitationsProgress({
    companyId,
  });

  const handleViewChange = (view: ScreeningView) => {
    setSearchParams(
      (prev) => {
        prev.set('view', view);
        return prev;
      },
      {replace: true}
    );
  };

  if (!isScreeningModuleActive || !isCultureInformationAvailable) {
    return <ScreeningModuleBanner text={screeningTooltipText} />;
  }

  return (
    <Grid>
      <CompanyScreeningHeader />
      <ScreeningStatusCountDisplay counts={totalCountPerStatus} />
      <Grid width='100%'>
        <Grid width='100%'>
          <ScreeningViewToggle value={selectedView} onChange={handleViewChange} />
        </Grid>
      </Grid>
      <Grid width='100%' sx={viewToggleContentStyles}>
        {isApplicantsView && (
          <>
            <Grid width='100%'>
              <ApplicantScreeningFilters
                locationOptions={createOptions(filtersOptions?.locations || [])}
                statusOptions={statuses}
                positionOptions={createOptions(filtersOptions?.jobPostings || [])}
                departmentOptions={(filtersOptions?.departments || []).map((department) => ({
                  id: department.resourceId,
                  label: department.name[currentLanguage],
                }))}
                initialStatus={initialStatuses}
                initialLocation={initialLocations}
                initialPosition={initialPositions}
                initialDepartment={initialDepartments}
                filterBy={filterBy}
              />
            </Grid>
            <Grid width='100%' sx={{paddingTop: '21px', paddingBottom: '40px'}}>
              <Divider />
            </Grid>
          </>
        )}
        <Grid width='100%' display='flex' justifyContent='flex-end' gap={2}>
          <EmpionButton
            sx={{
              padding: '8px 16px',
              marginBottom: '20px',
            }}
            onClick={() => setInviteApplicant(true)}
            startIcon={<PersonIcon />}
          >
            <Typography
              variant='h4'
              sx={{
                color: 'white',
              }}
            >
              {t('screeningModule.inviteApplicant')}
            </Typography>
          </EmpionButton>
          <EmpionButton
            sx={{
              padding: '8px 16px',
              marginBottom: '20px',
            }}
            onClick={() => setBatchInvitationsOpen(true)}
            startIcon={<GroupAddIcon />}
          >
            <Typography
              variant='h4'
              sx={{
                color: 'white',
              }}
            >
              {t('screeningModule.batchInvitations')}
            </Typography>
          </EmpionButton>
        </Grid>

        {isApplicantsView &&
          (isLoading ? (
            <LoadingContainer minHeight='400px' />
          ) : (
            <ApplicantsScreeningTable
              page={tablePage}
              onPageChange={setTablePage}
              screeningApplicants={screeningApplicants}
              pagination={pagination}
              totalCountPerStatus={totalCountPerStatus}
              hasNext={hasNext}
              getNext={getNext}
              refetch={refreshData}
              isLoading={screeningIsLoading}
            />
          ))}
        {!isApplicantsView && (
          <BatchInvitationsTable
            data={batchInvitations}
            isLoading={isBatchInvitationsLoading}
            highlightedBatchId={lastConfirmedBatchId}
            onDetails={openDetails}
          />
        )}
        <InviteApplicantModal
          open={inviteApplicant}
          onClose={() => setInviteApplicant(false)}
          refetch={refreshData}
          refetchFilters={refetchFilters}
          filterOptions={filtersOptions}
        />
        <BatchInvitationsModal
          open={batchInvitationsOpen}
          onClose={() => setBatchInvitationsOpen(false)}
          filterOptions={filtersOptions}
          refetch={refreshData}
          onConfirmSuccess={(batchId) => {
            handleViewChange('batch_invitations');
            setLastConfirmedBatchId(batchId);
            refetchBatchInvitations();
          }}
        />
        <BatchInvitationDetailsModal
          open={isDetailsOpen}
          onClose={closeDetails}
          details={detailsResponse}
          isLoading={isDetailsLoading}
          error={detailsError}
        />
      </Grid>
    </Grid>
  );
};
